import { gql } from '@apollo/client/core'
import { AVAILABILITY_BADGE_DISPLAY_TYPES, SALES_ITEM_RESTRICTION_TYPES } from 'src/models/inventory-strategy/InventoryStrategy'
import Model from 'src/models/Model'
import { BUTTON_TYPES as REGISTER_BUTTON_TYPES } from 'src/models/register/RegisterButton'
import { reactive } from 'vue'

export default async ({ app }) => {
  const salesRestrictions = {
    fetchItemVariationAvailabilitiesAndConstraints: null,
    fetchModifierAvailabilitiesAndConstraints: null,
    itemVariationObservationResponse: null,
    modifierObservationResponse: null
  }

  salesRestrictions.unsubscribe = () => {
    if (!_.isNil(salesRestrictions.itemVariationObservationResponse)) {
      salesRestrictions.itemVariationObservationResponse.unsubscribe()
    }

    if (!_.isNil(salesRestrictions.modifierObservationResponse)) {
      salesRestrictions.modifierObservationResponse.unsubscribe()
    }
  }

  salesRestrictions.subscribe = () => {
    if (!app.config.globalProperties.$cable) return

    salesRestrictions.unsubscribe()

    const itemVariationQuery = gql`subscription {
      itemVariationAvailabilitiesAndConstraintsUpdated {
        data
      }
    }`

    const itemVariationObserver = app.apolloProvider.defaultClient.subscribe({
      query: itemVariationQuery
    })

    salesRestrictions.itemVariationObservationResponse = itemVariationObserver.subscribe({
      next: _.debounce((data) => {
        if (_.isNil(data?.data?.itemVariationAvailabilitiesAndConstraintsUpdated)) return

        salesRestrictions.fetchItemVariationAvailabilitiesAndConstraints?.()
      }, 5000),
      error (error) {
        console.error(error)
      }
    })

    const modifierQuery = gql`subscription {
      modifierAvailabilitiesAndConstraintsUpdated {
        data
      }
    }`

    const modifierObserver = app.apolloProvider.defaultClient.subscribe({
      query: modifierQuery
    })

    salesRestrictions.modifierObservationResponse = modifierObserver.subscribe({
      next: _.debounce((data) => {
        if (_.isNil(data?.data?.modifierAvailabilitiesAndConstraintsUpdated)) return

        salesRestrictions?.fetchModifierAvailabilitiesAndConstraints?.()
      }, 5000),
      error (error) {
        console.error(error)
      }
    })
  }

  function getItemVariationAvailabilitiesAndConstraints () {
    return itemVariations.data
  }

  function setItemVariationAvailabilitiesAndConstraints (data) {
    itemVariations.data = data
  }

  function getModifierAvailabilitiesAndConstraints () {
    return modifiers.data
  }

  function setModifierAvailabilitiesAndConstraints (data) {
    modifiers.data = data
  }

  const itemVariations = reactive({
    data: null
  })

  const modifiers = reactive({
    data: null
  })

  function getSalesItem ({
    button,
    item,
    itemVariation,
    modifier
  }) {
    /**
     * We pass item with modifier, so we first need to check for modifier
     * explicitly.
     */
    if (modifier) return modifier

    return button || item || itemVariation
  }

  function buttonAvailableStock ({
    button,
    deliveryType,
    screen
  }) {
    return button.availableStock({
      deliveryType,
      itemVariationAvailabilitiesAndConstraints: getItemVariationAvailabilitiesAndConstraints(),
      modifiweerVariationAvailabilitiesAndConstraints: getModifierAvailabilitiesAndConstraints(),
      screen
    })
  }

  function modifierAvailableStock ({
    deliveryType,
    item,
    modifier,
    screen,
    selectedItemVariationId
  }) {
    return modifier.availableStock({
      availabilitiesAndConstraints: getModifierAvailabilitiesAndConstraints(),
      deliveryType,
      item,
      screen,
      selectedItemVariationId
    })
  }

  function itemAvailableStock ({
    deliveryType,
    item,
    screen
  }) {
    return item.availableStock({
      deliveryType,
      itemVariationAvailabilitiesAndConstraints: getItemVariationAvailabilitiesAndConstraints(),
      modifiweerVariationAvailabilitiesAndConstraints: getModifierAvailabilitiesAndConstraints(),
      screen
    })
  }

  function itemVariationAvailableStock ({
    deliveryType,
    itemVariation,
    screen,
    selectedModifierIds
  }) {
    return itemVariation.availableStock({
      availabilitiesAndConstraints: getItemVariationAvailabilitiesAndConstraints(),
      deliveryType,
      screen,
      selectedModifierIds
    })
  }

  function availableStock ({
    button,
    deliveryType,
    item,
    itemVariation,
    modifier,
    screen,
    selectedModifierIds,
    selectedItemVariationId
  }) {
    const salesItem = getSalesItem({ button, item, itemVariation, modifier })

    switch (salesItem.constructor.name) {
      case 'RegisterButton':
        return buttonAvailableStock({
          button,
          deliveryType,
          screen
        })
      case 'Modifier':
        return modifierAvailableStock({
          deliveryType,
          item,
          modifier,
          screen,
          selectedItemVariationId
        })
      case 'Item':
        return itemAvailableStock({
          deliveryType,
          item,
          screen
        })
      case 'ItemVariation':
        return itemVariationAvailableStock({
          deliveryType,
          itemVariation,
          screen,
          selectedModifierIds
        })
      default:
        return null
    }
  }

  function buttonOutOfStock ({
    button,
    deliveryType,
    screen
  }) {
    return button.isOutOfStock({
      deliveryType,
      itemVariationAvailabilitiesAndConstraints: getItemVariationAvailabilitiesAndConstraints(),
      modifiweerVariationAvailabilitiesAndConstraints: getModifierAvailabilitiesAndConstraints(),
      screen
    })
  }

  function modifierOutOfStock ({
    deliveryType,
    item,
    modifier,
    screen,
    selectedItemVariationId
  }) {
    return modifier.isOutOfStock({
      availabilitiesAndConstraints: getModifierAvailabilitiesAndConstraints(),
      deliveryType,
      item,
      screen,
      selectedItemVariationId
    })
  }

  function itemOutOfStock ({
    deliveryType,
    item,
    screen
  }) {
    return item.isOutOfStock({
      deliveryType,
      itemVariationAvailabilitiesAndConstraints: getItemVariationAvailabilitiesAndConstraints(),
      modifiweerVariationAvailabilitiesAndConstraints: getModifierAvailabilitiesAndConstraints(),
      screen
    })
  }

  function itemVariationOutOfStock ({
    deliveryType,
    itemVariation,
    screen,
    selectedModifierIds
  }) {
    return itemVariation.isOutOfStock({
      availabilitiesAndConstraints: getItemVariationAvailabilitiesAndConstraints(),
      deliveryType,
      screen,
      selectedModifierIds
    })
  }

  function isOutOfStock ({
    button,
    deliveryType,
    item,
    itemVariation,
    modifier,
    screen,
    selectedModifierIds,
    selectedItemVariationId
  }) {
    const salesItem = getSalesItem({ button, item, itemVariation, modifier })

    switch (salesItem.constructor.name) {
      case 'RegisterButton':
        return buttonOutOfStock({
          button,
          deliveryType,
          screen
        })
      case 'Modifier':
        return modifierOutOfStock({
          deliveryType,
          item,
          modifier,
          screen,
          selectedItemVariationId
        })
      case 'Item':
        return itemOutOfStock({
          deliveryType,
          item,
          screen
        })
      case 'ItemVariation':
        return itemVariationOutOfStock({
          deliveryType,
          itemVariation,
          screen,
          selectedModifierIds
        })
      default:
        return false
    }
  }

  function reduceOpacity ({
    button,
    deliveryType,
    inventoryStrategy,
    item,
    itemVariation,
    modifier,
    screen,
    selectedModifierIds,
    selectedItemVariationId
  }) {
    const salesItem = getSalesItem({ button, item, itemVariation, modifier })

    if (salesItem.constructor.name === 'RegisterButton' && salesItem.type !== REGISTER_BUTTON_TYPES.ITEM && salesItem.type !== REGISTER_BUTTON_TYPES.ITEM_VARIATION) return false

    if (salesItem.salesUnavailable || salesItem.salesDisabled) return true

    if (!inventoryStrategy?.inventoryRestrictionType) return false

    return isOutOfStock({
      button,
      deliveryType,
      item,
      itemVariation,
      modifier,
      screen,
      selectedModifierIds,
      selectedItemVariationId
    })
  }

  function showAvailabilityBadge ({
    button,
    deliveryType,
    inventoryStrategy,
    item,
    itemVariation,
    modifier,
    screen,
    selectedModifierIds,
    selectedItemVariationId
  }) {
    if (!app.config.globalProperties.$register) return false

    const salesItem = getSalesItem({ button, item, itemVariation, modifier })

    if (!salesItem) return false

    if (salesItem.salesUnavailable || salesItem.salesDisabled) return false

    if (!inventoryStrategy) return false

    if (!inventoryStrategy.inventoryRestrictionType) return false

    if (!inventoryStrategy.availabilityBadgeDisplayType) return false

    const available = availableStock({
      button,
      deliveryType,
      item,
      itemVariation,
      modifier,
      screen,
      selectedModifierIds,
      selectedItemVariationId
    })

    switch (inventoryStrategy.availabilityBadgeDisplayType) {
      case AVAILABILITY_BADGE_DISPLAY_TYPES.ALWAYS:
        return available !== null
      case AVAILABILITY_BADGE_DISPLAY_TYPES.LOW_STOCK:
        return typeof available === 'string' && +available < inventoryStrategy.availabilityBadgeLowStockThreshold
      case AVAILABILITY_BADGE_DISPLAY_TYPES.OUT_OF_STOCK:
        return isOutOfStock({
          button,
          deliveryType,
          item,
          itemVariation,
          modifier,
          screen,
          selectedModifierIds,
          selectedItemVariationId
        })
      default:
        return true
    }
  }

  function availabilityBadgeColor ({
    button,
    deliveryType,
    inventoryStrategy,
    item,
    itemVariation,
    modifier,
    screen,
    selectedModifierIds,
    selectedItemVariationId
  }) {
    if (isOutOfStock({
      button,
      deliveryType,
      item,
      itemVariation,
      modifier,
      screen,
      selectedModifierIds,
      selectedItemVariationId
    })) return 'negative'

    if (availableStock({
      button,
      deliveryType,
      item,
      itemVariation,
      modifier,
      screen,
      selectedModifierIds,
      selectedItemVariationId
    }) < inventoryStrategy.availabilityBadgeLowStockThreshold) return 'warning'

    return 'positive'
  }

  function checkForSalesRestrictions ({
    button,
    currentUser,
    deliveryType,
    inventoryStrategy,
    item,
    itemVariation,
    modifier,
    screen,
    selectedModifierIds,
    selectedItemVariationId,
    successCallback
  }) {
    const salesItem = getSalesItem({ button, item, itemVariation, modifier })

    if (salesItem.salesUnavailable || salesItem.salesDisabled) {
      app.config.globalProperties.$q.dialog({
        title: 'Unavailable',
        message: 'Sales for this item are disabled.',
        ok: {
          label: 'Cancel',
          color: 'positive',
          outline: true
        }
      })

      return
    }

    if (
      _.includes(SALES_ITEM_RESTRICTION_TYPES.INTERVENTION, inventoryStrategy?.inventoryRestrictionType) &&
      isOutOfStock({
        button,
        deliveryType,
        item,
        itemVariation,
        modifier,
        screen,
        selectedModifierIds,
        selectedItemVariationId
      })
    ) {
      app.config.globalProperties.$q.dialog({
        title: 'Inventory Unavailable',
        message: 'This item is out of stock.',
        ok: {
          label: 'Cancel',
          color: 'positive',
          outline: true
        },
        ...(inventoryStrategy.inventoryRestrictionType === SALES_ITEM_RESTRICTION_TYPES.RESTRICT
          ? {}
          : {
              cancel: {
                label: 'Continue',
                color: 'negative'
              }
            })
      }).onCancel(inventoryStrategy.inventoryRestrictionType === SALES_ITEM_RESTRICTION_TYPES.RESTRICT
        ? undefined
        : () => {
            inventoryStrategy.inventoryRestrictionType === SALES_ITEM_RESTRICTION_TYPES.ADMIN_ONLY && !currentUser.admin ? this.$emit('request-authorization', () => successCallback(true)) : successCallback()
          })
    } else if (salesItem.requiresAuthorization && !currentUser.admin) {
      this.$emit('request-authorization', () => successCallback(true))
    } else {
      successCallback()
    }
  }

  app.config.globalProperties.$salesRestrictions = salesRestrictions
  app.config.globalProperties.$setItemVariationAvailabilitiesAndConstraints = setItemVariationAvailabilitiesAndConstraints
  app.config.globalProperties.$setModifierAvailabilitiesAndConstraints = setModifierAvailabilitiesAndConstraints
  app.config.globalProperties.$availableStock = availableStock
  app.config.globalProperties.$isOutOfStock = isOutOfStock
  app.config.globalProperties.$reduceOpacity = reduceOpacity
  app.config.globalProperties.$showAvailabilityBadge = showAvailabilityBadge
  app.config.globalProperties.$availabilityBadgeColor = availabilityBadgeColor
  app.config.globalProperties.$checkForSalesRestrictions = checkForSalesRestrictions

  Model.prototype.$isOutOfStock = isOutOfStock
}
